// src/Pages/Commerce/commerceShared.js
//
// Shared visual primitives + brand tokens for the Blitz Commerce pages
// (ProductManagement, CommerceTransactions, CreateProductLinkDialog,
// ProductCheckoutPage). Kept in one place so the "Commerce" surface reads
// as one consistent product instead of three independently-styled pages.

import React, { useRef, useState } from "react";
import { Box, Paper, Typography, Skeleton, IconButton, CircularProgress } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import client from "../../API";

// Same sky-blue identity ProductCheckoutPage already established for the
// buyer-facing hosted checkout — reused here so merchant-facing Commerce
// pages feel like the same product as the checkout page their buyers see.
export const COMMERCE_BRAND = {
  sky: "#0ea5e9",
  skyDark: "#0284c7",
  skyDeep: "#075985",
  skyLight: "#7dd3fc",
  ink: "#0f172a",
};

export const formatCurrency = (value) => {
  const num = parseFloat(value || 0);
  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatDate = (value, opts) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(
    undefined,
    opts || { month: "short", day: "numeric", year: "numeric" },
  );
};

export const StatCard = ({ icon, label, value, sublabel, loading, accent }) => {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: accent
            ? `${theme.palette[accent].main}1A`
            : theme.palette.action.hover,
          color: accent ? theme.palette[accent].main : theme.palette.text.primary,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={80} height={28} />
        ) : (
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>
            {value}
          </Typography>
        )}
        {sublabel && !loading && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {sublabel}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const MAX_IMAGE_BYTES = 4.5 * 1024 * 1024;

// Reuses the existing generic file-upload endpoint (client.files.post,
// POST /files/upload) rather than standing up Commerce-specific upload
// infra — same base64-over-JSON contract already used for creator pfps
// and campaign attachments (see Components/tools/uploadfile.js). Returns
// a plain URL string so callers don't need to know the response shape.
export function uploadCommerceImage(file) {
  if (!file) return Promise.reject(new Error("No file selected."));
  if (file.size > MAX_IMAGE_BYTES) {
    return Promise.reject(new Error("Image must be under 4.5 MB."));
  }
  if (!file.type?.startsWith("image/")) {
    return Promise.reject(new Error("Please choose an image file."));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read that file."));
    reader.onload = () => {
      const base64 = String(reader.result).split(",")[1];
      client.files
        .post({
          file: { name: file.name, content: base64, type: file.type, file_type: "image" },
        })
        .then((data) => resolve(data?.file_url || data?.url || null))
        .catch(reject);
    };
    reader.readAsDataURL(file);
  });
}

// Single image upload slot — click to pick a file, shows a thumbnail once
// uploaded with a remove (x) overlay. Used for both the primary image and
// each secondary gallery slot in CreateProductLinkDialog; `size` lets the
// primary slot render larger than the secondary row.
export function ImageSlot({ url, onUploaded, onRemove, size = 88, label }) {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setUploading(true);
    uploadCommerceImage(file)
      .then((uploadedUrl) => onUploaded(uploadedUrl))
      .catch((err) => setError(err.message || "Upload failed"))
      .finally(() => setUploading(false));
  };

  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      <Box
        onClick={() => !uploading && inputRef.current?.click()}
        sx={{
          position: "relative",
          width: size,
          height: size,
          borderRadius: 2,
          overflow: "hidden",
          cursor: uploading ? "default" : "pointer",
          border: `1.5px dashed ${alpha(theme.palette.text.primary, url ? 0 : 0.25)}`,
          bgcolor: url ? "transparent" : alpha(theme.palette.action.hover, 0.5),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "&:hover": { borderColor: uploading ? undefined : alpha(theme.palette.primary.main, 0.5) },
        }}
      >
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} />
        {uploading ? (
          <CircularProgress size={20} thickness={5} />
        ) : url ? (
          <Box
            component="img"
            src={url}
            alt=""
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <AddPhotoAlternateOutlinedIcon sx={{ color: "text.disabled", fontSize: size > 60 ? 26 : 18 }} />
        )}

        {url && !uploading && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              width: 20,
              height: 20,
              bgcolor: alpha("#000", 0.55),
              color: "#fff",
              "&:hover": { bgcolor: alpha("#000", 0.75) },
            }}
          >
            <CloseOutlinedIcon sx={{ fontSize: 12 }} />
          </IconButton>
        )}
      </Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          {label}
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error" sx={{ fontSize: "0.65rem", maxWidth: size, textAlign: "center" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export const EmptyState = ({ icon, title, description, action }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      py: 6,
      px: 2,
      gap: 1,
    }}
  >
    <Box sx={{ opacity: 0.4, fontSize: 40, display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
      {description}
    </Typography>
    {action}
  </Box>
);
